/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

const possibleLocales = [
	"ar",
	"bg",
	"cs",
	"da",
	"de",
	"en-US",
	"es-ES",
	"es-MX",
	"fi",
	"fil",
	"fr",
	"hi",
	"hu",
	"id",
	"it",
	"ja",
	"ko",
	"ms",
	"nl",
	"no",
	"pl",
	"pt",
	"pt-BR",
	"ro",
	"ru",
	"sv",
	"th",
	"tr",
	"uk",
	"vi",
	"zh-CN",
	"zh-Hant",
	"zh-TW",
];

export const getLocalesRegex = (locales: string[], strict = false): RegExp =>
	new RegExp(`^/(${locales.join("|")})(/|$)`, !strict ? "i" : undefined);

export const checkPathnameHasLocale = (pathname: string, locales: string[], strict = false): boolean => {
	if (typeof pathname !== "string" || !pathname?.length) return false;
	if (!locales?.length) return false;
	return getLocalesRegex(locales, strict).test(pathname);
};

export const replaceUnsupportedLocale = (
	pathname: string,
	locales: string[],
	defaultLocale: string,
): string | boolean => {
	const unsupportedLocales = possibleLocales.filter((loc) => !locales.includes(loc));
	if (unsupportedLocales.length) {
		if (!checkPathnameHasLocale(pathname, unsupportedLocales)) return false;
		const re = new RegExp(`^/(${unsupportedLocales.join("|")})(/?$|/.+)`);
		const match = pathname.match(re);
		if (match) {
			const rest = match[2] === "/" ? "" : match[2];
			return `/${defaultLocale}${rest}`;
		}
	}
	return false;
};

export const langToLocale = (lang: string, locales: string[]): string | boolean => {
	if (typeof lang !== "string" || !lang?.length) return false;
	if (!locales?.length) return false;
	const needle = lang.trim().toLowerCase();
	if (!needle?.length) return false;
	return locales.find((locale) => locale.toLowerCase() === needle) || false;
};

export const removePathnameLocale = (pathname: string, locales: string | string[], strict = false): string => {
	if (typeof pathname !== "string" || !pathname?.length) return "/";
	if (!locales?.length) return pathname;
	if (typeof locales === "string") locales = [locales];
	return pathname.replace(getLocalesRegex(locales, strict), `/`);
};

export const pathnameStartsWithLocale = (pathname: string, locale: string): boolean => {
	if (typeof pathname !== "string" || !pathname?.length) return false;
	if (typeof locale !== "string" || !locale?.length) return false;
	return pathname.startsWith(`/${locale}/`) || pathname === `/${locale}`;
};

export const pathnameToLocale = (pathname: string, locales: string[]): string | boolean => {
	if (typeof pathname !== "string" || !pathname?.length) return false;
	if (!locales?.length) return false;
	const stack = pathname.toLowerCase();
	if (!stack?.length) return false;
	return locales?.find((locale) => pathnameStartsWithLocale(stack, locale.toLowerCase())) || false;
};

/**
 * Comprehensive mapping of unsupported/legacy locale codes to their canonical equivalents.
 * All keys are lowercase. Used as input to buildUnsupportedLocaleMapping() — never use directly
 * in site configs or middleware, as it may contain entries invalid for a given site.
 *
 * Layer 1 of locale defense: remaps known aliases (de-DE → de, zh-Hans → zh-CN, etc.)
 * Layer 2: replaceUnsupportedLocale / possibleLocales (globally-known but site-unsupported codes → defaultLocale)
 * Layer 3: Accept-Language / defaultLocale fallback
 */
const commonUnsupportedLocaleMapping: Record<string, string[]> = {
	// Bare language codes
	en: ["en-US"],
	es: ["es-ES"],
	pt: ["pt-BR", "pt"],
	zh: ["zh-CN", "zh-Hans"],
	// Latin America Spanish
	"es-419": ["es-MX"],
	// Cross-variant Spanish — sites may support one but not the other
	"es-es": ["es-ES", "es-MX"],
	"es-mx": ["es-MX", "es-ES"],
	// French Canadian — no fr-CA supported, nearest is fr
	"fr-ca": ["fr"],
	// Cross-variant Portuguese — legalhub has bare pt; others have pt-BR
	"pt-pt": ["pt", "pt-BR"],
	"pt-br": ["pt-BR", "pt"],
	// Simplified Chinese variants — dev-eg uses zh-Hans; others use zh-CN
	"zh-hans": ["zh-Hans", "zh-CN"],
	"zh-cn": ["zh-CN", "zh-Hans"],
	"zh-sg": ["zh-Hans", "zh-CN"],
	// Traditional Chinese variants — epicgames/legalhub have zh-Hant; others fall back to zh-CN
	"zh-hant": ["zh-Hant", "zh-TW", "zh-CN"],
	"zh-tw": ["zh-Hant", "zh-CN"],
	"zh-hk": ["zh-Hant", "zh-CN"],
	"zh-mo": ["zh-Hant", "zh-CN"],
	// Region-specific → base
	"de-de": ["de"],
	"fr-fr": ["fr"],
	"ja-jp": ["ja"],
	"ko-kr": ["ko"],
	"ar-sa": ["ar"],
	"ru-ru": ["ru"],
	"it-it": ["it"],
	"pl-pl": ["pl"],
	"tr-tr": ["tr"],
	"th-th": ["th"],
	// Legalhub-specific region codes
	"nb-no": ["no"],
	nb: ["no"],
	"no-no": ["no"],
	"sv-se": ["sv"],
	"da-dk": ["da"],
	"fi-fi": ["fi"],
	"nl-nl": ["nl"],
	"vi-vn": ["vi"],
	"hi-in": ["hi"],
	"id-id": ["id"],
	in: ["id"], // deprecated ISO 639-1 code for Indonesian (replaced by "id")
	"ms-my": ["ms"],
	"uk-ua": ["uk"],
	"fil-ph": ["fil"],
	"ro-ro": ["ro"],
	"hu-hu": ["hu"],
	"cs-cz": ["cs"],
	"bg-bg": ["bg"],
};

/**
 * Builds a site-specific unsupported locale mapping filtered to only include entries where:
 * - the key is NOT already a valid locale for this site (avoids remapping valid paths)
 * - the target IS a valid locale for this site (avoids redirecting to unsupported locales)
 *
 * Site-specific overrides are merged after filtering. A warning is logged if an override
 * target is not in siteLocales, as this is almost certainly a bug.
 *
 * @param siteLocales - The site's supported locales array
 * @param options - Optional configuration object
 * @param options.overrides - Site-specific additions/overrides (keys should be lowercase)
 * @param options.defaultLocale - Fallback locale when no target matches
 *
 * @example
 * // i18n/config.ts
 * export const unsupportedLocaleMapping = buildUnsupportedLocaleMapping(locales, {
 *   defaultLocale,
 *   overrides: { "zh-cn": "zh-Hans" }, // site uses zh-Hans instead of zh-CN
 * });
 */
export function buildUnsupportedLocaleMapping(
	siteLocales: readonly string[],
	options?: { overrides?: Record<string, string>; defaultLocale?: string },
): Record<string, string> {
	const { overrides, defaultLocale } = options ?? {};
	const lowerSiteLocales = siteLocales.map((l) => l.toLowerCase());
	const base = Object.fromEntries(
		Object.entries(commonUnsupportedLocaleMapping)
			.filter(([key]) => !lowerSiteLocales.includes(key))
			.flatMap(([key, targets]) => {
				const match = targets.find((t) => siteLocales.includes(t));
				if (match) return [[key, match]];
				// No target matches — fall back to defaultLocale so the code is still
				// recognised as a locale prefix and stripped instead of 404ing.
				if (defaultLocale) return [[key, defaultLocale]];
				return [];
			}),
	);
	const filteredOverrides: Record<string, string> = {};
	if (overrides) {
		for (const [key, target] of Object.entries(overrides)) {
			if (lowerSiteLocales.includes(key.toLowerCase())) {
				console.warn(`[buildUnsupportedLocaleMapping] override key "${key}" is already a valid locale — skipping`);
				continue;
			}
			if (!siteLocales.includes(target)) {
				console.warn(
					`[buildUnsupportedLocaleMapping] override "${key}" → "${target}" is invalid: "${target}" is not in siteLocales`,
				);
			}
			filteredOverrides[key] = target;
		}
	}
	return { ...base, ...filteredOverrides };
}
